import Group from 'components/utils/Group'
import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import StarRatingStar from './StarRatingStar'

type StarKind = ComponentProps<typeof StarRatingStar>['kind']

interface Props {
  /**
   * Defines the maximum number of stars.
   */
  max: number
  /**
   * Sets the number of filled stars.
   *
   * Decimals will be presented as a half star.
   */
  value: number
  size?: ComponentProps<typeof StarRatingStar>['size']
}

function StarRating(props: Props) {
  const {
    max,
    value,
    size,
  } = props

  const starKinds = useMemo<Array<StarKind>>(() => {
    if (!max) {
      return []
    }

    if (!value) {
      return Array.from({ length: max }).map<StarKind>(() => 'empty')
    }

    if (value >= max) {
      return Array.from({ length: max }).map<StarKind>(() => 'filled')
    }

    const kinds: Array<StarKind> = []

    const filled = Math.floor(value)

    for (let i = 0; i < filled; i++) {
      kinds.push('filled')
    }

    // decimals are rounded up to represent a half star
    const half = Math.ceil(value - filled)

    if (half) kinds.push('half')

    const empty = max - filled - half

    for (let i = 0; i < empty; i++) {
      kinds.push('empty')
    }

    return kinds
  }, [value, max])

  return <Group direction="horizontal" gap={2}>
    {starKinds.map((starKind, i) => <StarRatingStar key={i} kind={starKind} size={size} />)}
  </Group>
}

export default StarRating
